import {Component, computed, inject, input} from '@angular/core';
import {WineProduct} from '@core/interfaces/generated/graphql';
import {RouterLink} from '@angular/router';
import {RouterContextDirective} from '@core/directives/router-context.directive';
import {formatNumber} from '@angular/common';
import {environment} from '@env/environment';
import {ReadMoreComponent} from '@layout/components/general/read-more/read-more.component';
import {GridService} from '@core/services/grid.service';
import {RoutingService, UrlFormatted} from '@core/services/routing.service';

type ProductData = {
  label: string;
  value: {
    label?: string | null;
    url?: string | null;
    externalUrl?: string | null;
  }[];
}

@Component({
  selector: 'app-product-data-section',
  templateUrl: './product-data-section.component.html',
  imports: [
    RouterLink,
    RouterContextDirective,
    ReadMoreComponent
  ],
  styleUrl: './product-data-section.component.scss'
})
export class ProductDataSectionComponent {

  gridService = inject(GridService);
  routingService = inject(RoutingService);

  product = input.required<WineProduct | null>();

  title = computed(() => {
    const product = this.product();
    if (!product) {
      return $localize`Produktdata`;
    }
    return product.meta?.productType === 'Vin' ? $localize`Vindata` : $localize`Produktdata`;
  });

  productData = computed<ProductData[]>(() => {
    const product = this.product();
    if (!product) {
      return [];
    }

    const data = [];

    if ((product.meta?.grapesV2?.length ?? 0) > 0) {
      data.push({
        label: $localize`Druer`,
        value: product.meta?.grapesV2?.map(grape => {
          return {
            label: grape?.title,
            url: grape?.urlPath
          };
        }) ?? []
      });
    }

    if (product.meta?.countryEnt?.title) {
      const title = product.meta?.productType === 'Vin' ? $localize`Vinen kommer fra` : $localize`Produktet kommer fra`;

      const locationEntities = [
        product.meta?.countryEnt,
        product.meta?.areaEnt,
        product.meta?.districtEnt
      ].filter(entity => entity?.id && entity?.title).map(entity => ({
        label: entity?.title,
        url: entity?.urlPath
      }));

      if (locationEntities.length > 0) {
        data.push({
          label: title,
          value: locationEntities
        });
      }
    }

    if (product.meta?.producer?.title) {
      data.push({
        label: $localize`Producent`,
        value: [{
          label: product.meta?.producer?.title,
          url: product.meta?.producer?.urlPath
        }]
      });
    }

    if (product.meta?.vintage) {
      data.push({
        label: $localize`Årgang`,
        value: [{
          label: product.meta?.vintage.toString()
        }]
      });
    }

    if (product.meta?.contentLabel) {
      const title = product.meta?.measurementUnit === 'kg' ? $localize`Vægt` : $localize`Indhold`;
      data.push({
        label: title,
        value: [{
          label: product.meta?.contentLabel,
        }]
      });
    }

    if (product.meta?.alcohol) {
      data.push({
        label: $localize`Alkohol-%`,
        value: [{
          label: product.meta?.alcohol + ' %'
        }]
      });
    }

    if (product.meta?.serving) {
      data.push({
        label: $localize`Servering`,
        value: [{
          label: product.meta?.serving
        }]
      })
    }

    if (product.meta?.savingPotential) {
      data.push({
        label: $localize`Gemmepotentiale`,
        value: [{
          label: product.meta?.savingPotential
        }]
      });
    }

    if ((product.meta?.barrelTypes?.length ?? 0) > 0) {
      data.push({
        label: $localize`Lagring`,
        value: product.meta?.barrelTypes?.map(barrel => {
          return {
            label: barrel?.title,
          };
        }) ?? []
      });
    }

    if (product.closure) {
      data.push({
        label: $localize`Proptype`,
        value: [{
          label: product.closure
        }]
      });
    }

    if (product.packaging) {
      data.push({
        label: $localize`Emballage`,
        value: [{
          label: product.packaging
        }]
      });
    }

    if (product.meta?.nutritionalInfoUrl) {
      data.push({
        label: $localize`Næringsindhold`,
        value: [{
          label: $localize`Se producentens varedeklaration her`,
          externalUrl: product.meta?.nutritionalInfoUrl
        }]
      });
    }

    if ((product.meta?.allergens?.length ?? 0) > 0) {
      data.push({
        label: $localize`Allergener`,
        value: product.meta?.allergens?.map(allergen => {
          return {
            label: allergen,
          };
        }) ?? []
      });
    }

    if (product.meta?.residualSugar) {
      data.push({
        label: $localize`Restsukker`,
        value: [{
          label: formatNumber(product.meta?.residualSugar / 100, environment.locale) + ' ' + $localize`g/L`
        }]
      });
    }

    if (product.meta?.acidContent) {
      data.push({
        label: $localize`Syreindhold`,
        value: [{
          label: formatNumber(product.meta?.acidContent / 100, environment.locale) + ' ' + $localize`g/L`
        }]
      });
    }

    return data;
  })

  getFormattedUrl(path: string | null | undefined): UrlFormatted {
    const defaultFormattedUrl = {
      isRelative: false,
      search: {},
      fullURL: '',
      hash: undefined,
      pathname: ''
    };
    if (!path) {
      return defaultFormattedUrl
    }
    const formattedUrl = this.routingService.getUrlFormattedObjectByPath(path);
    if (formattedUrl === undefined) {
      return defaultFormattedUrl
    }
    return formattedUrl;
  }

}
